// @ts-ignore
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ServiceCategory } from "../types";
import { WidgetHeader } from "../../../components/WidgetHeader";
import { useTranslation } from "react-i18next";

export default function ServicesView({
  categories,
  selectedCategory,
  selectedServices,
  searchQuery,
  onBack,
  onSelectCategory,
  onToggleService,
  onSearch,
  onContinue,
  ctaLabel,
  // NEW:
  categoryEmptyForMaster,
  onPickAnotherMaster,
  onGoToSpecialist,
  onGoToServices,
  onOpenClientDashboard,
  onOpenSupport,
  initialScrollCategoryId,
}: {
  categories: ServiceCategory[];
  selectedCategory: string;
  selectedServices: string[];
  searchQuery: string;
  onBack: () => void;
  onSelectCategory: (id: string) => void;
  onToggleService: (id: string) => void;
  onSearch: (q: string) => void;
  onContinue?: () => void;
  ctaLabel?: string;
  // NEW:
  categoryEmptyForMaster?: boolean;
  onPickAnotherMaster?: () => void;
  onGoToSpecialist: () => void;
  onGoToServices: () => void;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
  initialScrollCategoryId?: string | null;
}) {
  const { t } = useTranslation();
  // Build a derived "visible" list we can render all-at-once,
  // filtered by the search query (if any).
  const visibleCategories: ServiceCategory[] = React.useMemo(() => {
    const q = (searchQuery ?? "").trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((c: ServiceCategory) => ({
        ...c,
        services: (c.services ?? []).filter((s: any) =>
          s.name.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => (c.services?.length ?? 0) > 0);
  }, [categories, searchQuery]);

  // --- Categories Scroll Management ---
  const categoriesRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [scrollIndicator, setScrollIndicator] = React.useState({ left: 0, width: 0 });

  const checkScroll = React.useCallback(() => {
    const el = categoriesRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);

    if (scrollWidth > clientWidth) {
      const widthPercentage = (clientWidth / scrollWidth) * 100;
      const leftPercentage = (scrollLeft / scrollWidth) * 100;
      setScrollIndicator({ left: leftPercentage, width: widthPercentage });
    } else {
      setScrollIndicator({ left: 0, width: 0 });
    }
  }, []);

  React.useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll, categories, visibleCategories]);

  const scrollCategories = (direction: "left" | "right") => {
    const el = categoriesRef.current;
    if (!el) return;
    const amount = el.clientWidth / 2;
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  // --- Scrolling + active section tracking -----------------------------------
  // We know exactly which element scrolls (the inner pane). Anchor to it.
  const scrollPaneRef = React.useRef<HTMLDivElement | null>(null);
  const navRef = React.useRef<HTMLElement | null>(null);

  // Dynamically measure the sticky nav height instead of a hardcoded constant.
  // Adding a small buffer (4px) so the category header never tucks behind the nav.
  const getStickyHeight = React.useCallback(() => {
    return (navRef.current?.offsetHeight ?? 84) + 15;
  }, []);

  // Safe CSS.escape for ids (Safari 14 and some Androids lack it)
  const cssEscape = React.useCallback((v: string) => {
    // @ts-ignore
    return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(v) : v.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }, []);

  // Click a pill -> smooth scroll the pane to the section
  const scrollToCategory = (id: string) => {
    const pane = scrollPaneRef.current;
    const section = pane?.querySelector<HTMLElement>(`#svc-section-${cssEscape(id)}`);
    onSelectCategory(id); // reflect immediately for visual feedback
    if (!pane || !section) return;
    // Use getBoundingClientRect for reliable position relative to scroll pane
    const paneRect = pane.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const targetTop = pane.scrollTop + (sectionRect.top - paneRect.top) - getStickyHeight();
    pane.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  };

  // While scrolling, auto-select the pill for the section currently at/above the sticky line
  React.useEffect(() => {
    const pane = scrollPaneRef.current;
    if (!pane) return;

    const getSections = () =>
      Array.from(pane.querySelectorAll<HTMLElement>('[id^="svc-section-"]'));

    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const sections = getSections();
        if (!sections.length) return;
        const paneRect = pane.getBoundingClientRect();
        // Line we consider "active": top of pane + sticky nav height
        const stickyBottom = paneRect.top + getStickyHeight() + 1;
        // Choose the last section whose top is above the sticky bottom line
        let activeId = sections[0].id.replace("svc-section-", "");
        for (const sec of sections) {
          const secRect = sec.getBoundingClientRect();
          if (secRect.top <= stickyBottom) {
            activeId = sec.id.replace("svc-section-", "");
          } else {
            break;
          }
        }
        if (activeId && activeId !== selectedCategory) {
          onSelectCategory(activeId);
        }
      });
    };

    pane.addEventListener("scroll", onScroll, { passive: true });
    // Run once to set the correct pill on mount/content changes
    onScroll();
    return () => {
      pane.removeEventListener("scroll", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [visibleCategories.map(c => c.id).join("|"), getStickyHeight, onSelectCategory, selectedCategory]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedServices.length > 0) {
        const firstId = selectedServices[0];
        const el = document.getElementById(`service-item-${firstId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedServices]);

  // Scroll to the preselected category on first render after content is available
  React.useEffect(() => {
    if (!initialScrollCategoryId || !visibleCategories.length) return;
    // Small delay so the DOM sections have rendered and ref is attached
    const timer = setTimeout(() => {
      scrollToCategory(initialScrollCategoryId);
    }, 150);
    return () => clearTimeout(timer);
  }, [initialScrollCategoryId, visibleCategories.length > 0]);

  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          <WidgetHeader
            onBack={onBack}
            onGoToSpecialist={onGoToSpecialist}
            onGoToServices={onGoToServices}
            onOpenClientDashboard={onOpenClientDashboard}
            onOpenSupport={onOpenSupport}
          />

          {/* Scrollable content; bottom padding equals sticky bar height + safe area */}
          <div
            ref={scrollPaneRef}
            className="relative flex-1 overflow-y-auto p-6"
            style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
          >
            {/* Title */}
            <div className="pb-2">
              <h2 className="text-2xl font-bold text-foreground">{t("widget.services.choose", "Choose services")}</h2>
            </div>

            {/* Sticky category pills that act as anchors */}
            <nav
              ref={navRef}
              aria-label={t("widget.services.categories", "Service categories")}
              className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-2 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 border-b border-border/10 group"
            >
              <div className="relative flex items-center">
                <div
                  className={cn(
                    "absolute left-0 z-20 h-full flex items-center bg-gradient-to-r from-card via-card/90 to-transparent pr-4 pl-1 transition-opacity duration-200",
                    canScrollLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
                  )}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full shadow-sm bg-background"
                    onClick={() => scrollCategories("left")}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                </div>

                <div
                  ref={categoriesRef}
                  onScroll={checkScroll}
                  className="flex gap-2 overflow-x-auto scrollbar-hide w-full px-1"
                >
                  {visibleCategories.map((category) => (
                    <Button
                      key={category.id}
                      variant={selectedCategory === category.id ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all flex-shrink-0",
                        selectedCategory === category.id
                          ? "bg-foreground text-background hover:bg-foreground/90"
                          : "bg-muted/50 text-foreground hover:bg-muted/80 border-muted",
                      )}
                      onClick={() => scrollToCategory(category.id)}
                    >
                      {category.name}
                    </Button>
                  ))}
                </div>

                <div
                  className={cn(
                    "absolute right-0 z-20 h-full flex items-center bg-gradient-to-l from-card via-card/90 to-transparent pl-4 pr-1 transition-opacity duration-200",
                    canScrollRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
                  )}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full shadow-sm bg-background"
                    onClick={() => scrollCategories("right")}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {scrollIndicator.width > 0 && scrollIndicator.width < 100 && (
                <div className="mt-2 mx-auto w-1/3 h-1 bg-muted rounded-full overflow-hidden relative">
                  <div
                    className="absolute top-0 bottom-0 bg-primary/50 rounded-full transition-all duration-150"
                    style={{ left: `${scrollIndicator.left}%`, width: `${scrollIndicator.width}%` }}
                  />
                </div>
              )}
            </nav>

            {/* Search Bar */}
            <div className="pb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  placeholder={t("widget.services.search", "Search")}
                  value={searchQuery}
                  onChange={(e) => onSearch(e.target.value)}
                  className="pl-10 rounded-xl bg-muted/50 border-muted focus:border-primary/50 focus:ring-1 focus:ring-primary/20 w-full h-10"
                />
              </div>
            </div>

            {/* Services Content — ALL categories stacked with headers (anchor targets) */}
            <div className="space-y-8 category-listing">
              {visibleCategories.map((category) => (
                <section
                  key={category.id}
                  id={`svc-section-${category.id}`}
                  // ensure anchor scroll doesn't hide the header behind the sticky pills
                  style={{ scrollMarginTop: "84px" }}
                >
                  {/* Distinct header background */}
                  <h3 className="inline-block text-lg font-bold mb-3 rounded-lg px-3 py-1 backdrop-blur decoration-2 underline-offset-4">
                    {category.name}
                  </h3>
                  <div className="space-y-3">
                    {category.services.map((service) => (
                      <div
                        key={service.id}
                        id={`service-item-${service.id}`}
                        className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors scroll-mt-32"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-base text-foreground">{service.name}</h4>
                          {service.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {service.description}
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">{service.duration}</p>
                          <p className="text-lg font-bold text-foreground mt-2">
                            {service.priceLabel} {service.currency}
                          </p>
                        </div>
                        {/* Checkbox */}
                        <div
                          className={cn(
                            "w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all",
                            selectedServices.includes(service.id)
                              ? "bg-primary border-primary"
                              : "border-muted-foreground/30 hover:border-primary/50",
                          )}
                          onClick={() => onToggleService(service.id)}
                          role="checkbox"
                          aria-checked={selectedServices.includes(service.id)}
                          aria-label={t("widget.services.select_service", "Select service: {{name}}", { name: service.name })}
                        >
                          {selectedServices.includes(service.id) && <Check className="w-4 h-4 text-primary-foreground" />}
                        </div>
                      </div>
                    ))}
                    {category.services.length === 0 && (
                      <div className="text-sm text-muted-foreground">{t("widget.services.none_for_query", "No services for this search.")}</div>
                    )}
                  </div>
                </section>
              ))}
              {visibleCategories.length === 0 && (
                <div className="text-sm text-muted-foreground">{t("widget.common.nothing_found", "Nothing found")}</div>
              )}
            </div>
          </div>

          {/* Sticky CTA/status bar (participates in layout; no overlap) */}
          {selectedServices.length > 0 && onContinue && (
            <div
              className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
            >
              <div className="p-4 pt-3 space-y-3">
                <Button
                  className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-lg"
                  onClick={onContinue}
                  aria-label={ctaLabel ?? t("widget.datetime.choose", "Choose date & time")}
                >
                  {ctaLabel ?? t("widget.datetime.choose", "Choose date & time")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
